import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ClipboardList, Lightbulb, LayoutDashboard, FolderKanban, Users2, ShieldCheck, Settings2, Moon, Sun, LogOut, Radio, Banknote, Mail, CalendarCheck, FileCheck2, Gauge, Grid3x3, TrendingUp, Inbox, LifeBuoy } from "lucide-react";
import { QuickTour } from "./QuickTour";
import { useApi } from "../hooks/useApi";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../context/AuthContext";
import { useLiveStatus } from "../context/LiveContext";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

function navItems(auth) {
  return [...baseNavItems(auth), { to: "/help", label: "Help", icon: LifeBuoy, id: "help" }];
}

function baseNavItems({ isPartner, isAdmin, canEdit, user, partnerActive }) {
  if (isPartner && !partnerActive) return [{ to: `/onboarding/partners/${user.partner_id}`, label: "My onboarding", icon: ClipboardList, id: "my-onboarding" }];
  if (isPartner) {
    return [
      { to: `/partners/${user.partner_id}`, label: "My partner view", icon: Users2, id: "partner-view" },
      { to: "/projects", label: "My projects", icon: FolderKanban, id: "projects" },
      { to: "/drawdowns", label: "Drawdowns", icon: Banknote, id: "drawdowns" },
      { to: "/returns", label: "Quarterly returns", icon: CalendarCheck, id: "returns" },
      { to: `/my-variance/${user.partner_id}`, label: "My variance", icon: TrendingUp, id: "my-variance" },
      { to: "/insight", label: "Insight & exports", icon: Gauge, id: "insight" },
      { to: "/opportunities/new", label: "New project", icon: Lightbulb, id: "new-project" },
      { to: `/onboarding/partners/${user.partner_id}`, label: "Partner file", icon: ClipboardList, id: "my-onboarding" },
    ];
  }
  const insightHome = user.roles.some((r) => ["Approver", "Senior stakeholder"].includes(r));
  const items = [
    { to: insightHome ? "/dashboard" : "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
    { to: "/projects", label: "Projects", icon: FolderKanban, id: "projects" },
    { to: "/partners", label: "Partners", icon: Users2, id: "partners" },
    { to: "/drawdowns", label: "Drawdowns", icon: Banknote, id: "drawdowns" },
    { to: "/checks", label: "Checks", icon: ShieldCheck, id: "checks" },
    { to: "/quarter", label: "Quarter", icon: CalendarCheck, id: "quarter" },
    { to: "/insight", label: "Insight", icon: Gauge, id: "insight" },
  ];
  if (!user.roles.includes("Approver") || user.roles.length > 1) items.push({ to: "/gaps", label: "Fill the gaps", icon: Grid3x3, id: "gaps" });
  if (canEdit) items.push({ to: "/inbox", label: "Inbox", icon: Mail, id: "inbox" }, { to: "/onboarding", label: "Onboarding", icon: ClipboardList, id: "onboarding" });
  if (isAdmin) items.push({ to: "/admin", label: "Admin", icon: Settings2, id: "admin" });
  if (user.roles.some((r) => ["Approver", "GM", "Finance"].includes(r))) items.unshift({ to: "/approvals", label: "Approvals", icon: Inbox, id: "approvals", badge: true });
  return items;
}

function QueueBadge({ testid }) {
  const { data } = useApi("/my-queue/count");
  if (!data?.count) return null;
  return <span className="ml-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground" data-testid={testid}>{data.count}</span>;
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-1.5 whitespace-nowrap px-2 min-[1800px]:px-2.5 py-2 text-[13px] min-[1800px]:text-sm font-medium rounded-md transition-colors duration-150 [&>svg]:hidden min-[1600px]:[&>svg]:block ${
    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`;

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = user.name.split(" ").map((p) => p[0]).slice(0, 2).join("");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted transition-colors">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-foreground text-background text-xs font-bold font-heading">{initials}</span>
          <span className="hidden 2xl:block text-left leading-tight">
            <span className="block text-sm font-semibold" data-testid="current-user-name">{user.name}</span>
            <span className="block text-[11px] text-muted-foreground" data-testid="current-user-roles">{user.roles.join(" · ")}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-semibold">{user.name}</div>
          <div className="text-xs text-muted-foreground font-normal">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.roles.some((r) => ["Finance", "Admin"].includes(r)) && <DropdownMenuItem data-testid="user-menu-baselines" onClick={() => navigate("/baselines")}>
          <FileCheck2 className="mr-2 h-4 w-4" /> Baseline review
        </DropdownMenuItem>}
        {user.roles.includes("Admin") && <DropdownMenuItem data-testid="user-menu-admin" onClick={() => navigate("/admin")}>
          <Settings2 className="mr-2 h-4 w-4" /> Admin
        </DropdownMenuItem>}
        <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell() {
  const auth = useAuth();
  const live = useLiveStatus();
  const items = navItems(auth);
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-4 px-4 sm:px-6 lg:px-8">
          <NavLink to="/" className="flex shrink-0 items-center gap-3" data-testid="brand-home-link">
            <Logo className="h-8" />
            <span className="hidden sm:block whitespace-nowrap border-l pl-3 font-heading text-sm font-bold tracking-tight">JV Hub</span>
          </NavLink>
          <nav className="ml-2 hidden min-w-0 lg:flex items-center gap-0.5 overflow-x-auto grid-scroll" data-testid="main-nav">
            {items.filter((i) => i.id !== "admin" && i.id !== "help").map((i) => (
              <NavLink key={i.id} to={i.to} end={i.end} className={linkCls} data-testid={`nav-${i.id}`}>
                <i.icon className="h-4 w-4" /> {i.label}{i.badge && <QueueBadge testid="nav-approvals-badge" />}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <span data-testid="live-indicator" title={live ? "Live updates on" : "Reconnecting…"}
              className={`hidden min-[1800px]:flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${live ? "text-[hsl(var(--positive))] bg-[hsl(var(--positive)/0.1)]" : "text-muted-foreground bg-muted"}`}>
              <Radio className="h-3 w-3" /> {live ? "Live" : "Connecting"}
            </span>
            <NavLink to="/help" className={({ isActive }) => `hidden lg:flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`} data-testid="nav-help">
              <LifeBuoy className="h-4 w-4" /> Help
            </NavLink>
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={auth.toggleTheme} data-testid="theme-toggle-btn" aria-label="Toggle theme">
              {auth.theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <UserMenu />
          </div>
        </div>
        <nav className="lg:hidden flex gap-1 overflow-x-auto px-4 pb-2 pr-10 grid-scroll" style={{ maskImage: "linear-gradient(to right, black 85%, transparent)", WebkitMaskImage: "linear-gradient(to right, black 85%, transparent)" }} data-testid="mobile-nav">
          {items.map((i) => (
            <NavLink key={i.id} to={i.to} end={i.end} className={linkCls} data-testid={`mobile-nav-${i.id}`}>
              <i.icon className="h-4 w-4" /> {i.label}{i.badge && <QueueBadge testid="mobile-nav-approvals-badge" />}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <Outlet />
      </main>
      <QuickTour />
    </div>
  );
}
